// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Component } from './component';
import prepareUrl from './prepareUrl';
import render from './render';
import ce from './createElement';
import escapeHtml from './escapeHtml';
import api from './api';
import Locale from './Locale';
import addStatusMessage from './addStatusMessage';
import pageData from '../helpers/pageData';
import emptyFn from './emptyFn';

import './active-list.less';

/**
 * Example usage:
 *
 *     @example
 *     new ActiveList({
 *         renderTo: document.body,
 *         layout: 'responsivecolumn',
 *         data: [{
 *             title: 'Item 1',
 *             icon: 'class-icons/component-large.png',
 *             buttons: [{
 *                 title: 'Action 1',
 *                 icon: 'class-icons/class-large.png'
 *             }, {
 *                 title: 'Action 2',
 *                 icon: 'class-icons/singleton-large.png'
 *             }]
 *         }, {
 *             title: 'Item 2',
 *             icon: 'class-icons/component-large.png',
 *             buttons: [{
 *                 title: 'Action 1',
 *                 icon: 'class-icons/class-large.png'
 *             }, {
 *                 title: 'Action 2',
 *                 icon: 'class-icons/singleton-large.png'
 *             }]
 *         }]
 *     });
 */
export class ActiveList extends Component {
    _initConfiguration(config) {
        super._initConfiguration(config);

        this.navigate = this._getConfigParam('onNavigate', emptyFn);
        this.pageUrl = this._getConfigParam('pageUrl', location.href);

        this.layout = this._getConfigParam('layout', 'auto');
        if (typeof this.layout === 'string') {
            this.layout = {
                type: this.layout,
            };
        }

        this.itemActions = this._getConfigParam('itemActions', {});
        this.data = this._getConfigParam('data', []);

        this._cls = 'active-list';
    }

    _initComponentElement() {
        super._initComponentElement();
        render(this._componentElement, this.view());
    }

    view() {
        return ce('.active-list-wrap',
            this.itemsView(),
        );
    }

    itemsView() {
        if (!this.data.length) {
            return this.emptyView();
        }

        const items = this.data.map(this.itemView.bind(this));
        if (this.layout.type !== 'responsivecolumn') {
            return items;
        }

        let cls = ['.grid'];
        if (this.layout.stretched !== false) {
            cls.push('.grid-stretched');
        }
        cls = cls.concat((this.layout.columns || 'xl-2 xxl-3').split(' ').map(cls => `.grid-${cls}`));

        return ce(cls.join(''),
            items.map(item => ce('.grid-col', item)),
        );
    }

    emptyView() {
        return ce('p', this.lmsg('noObjects'));
    }

    itemView(item) {
        const itemClasses = [
            '.active-list-item',
        ];
        const types = [
            'danger',
            'warning',
            'inactive',
            'success',
        ];

        if (item.type && types.indexOf(item.type) > -1) {
            itemClasses.push(`.active-list-item-${item.type}`);
        }

        itemClasses.push('.active-list-item-expanded');

        return ce(`#active-list-item-${item.id}${itemClasses.join('')}`, { style: 'position:relative' },
            ce('.content-blocker', { style: 'display:none;' }),
            ce('.active-list-item-wrap',
                this.itemCaptionView(item),
                this.itemDetailsView(item),
            ),
        );
    }

    itemDetailsView(item) {
        const children = [];
        if (item.settings && item.settings.length) {
            children.push(this.itemSettingsView(item));
        }
        if (item.buttons && item.buttons.length) {
            children.push(this.itemToolsView(item));
        }

        if (!children.length) {
            return '';
        }

        return ce('.active-list-details',
            ce('.active-list-details-wrap',
                children,
            ),
        );
    }

    itemSettingsView(item) {
        return ce('.list',
            ce(`table#active-list-item-settings-table-${item.id}[width=100%][cellspacing=0]`,
                ce('tbody',
                    item.settings.map((settings, index) => ce(`tr${index % 2 ? '.even' : '.odd'}`,
                        ce('td.min',
                            ce('span.tooltipData', escapeHtml(settings.hint)),
                            ce(`a.s-btn.${settings.button}[data-method=post]`, { href: settings.url }, ce('span')),
                        ),
                        ce('td', escapeHtml(settings.name)),
                        ce('td', escapeHtml(settings.description)),
                    )),
                ),
            ),
        );
    }

    itemToolsView(item) {
        return ce('.tools-list-box',
            ce('.box-area',
                ce('.content',
                    ce('.content-area',
                        ce('ul.tools-list',
                            item.buttons.map(button => {
                                let onClick = null;
                                if (button.name && this.itemActions[button.name]) {
                                    onClick = event => {
                                        event.preventDefault();
                                        this.itemActions[button.name](item, event);
                                    };
                                } else if (button.handler) {
                                    onClick = typeof button.handler === 'function'
                                        ? button.handler
                                        : `(${button.handler}(event)); return false;`;
                                } else {
                                    onClick = this.navigate;
                                }

                                return ce('li.tools-item',
                                    ce(`a.tool-block${button.disabled ? '.disabled' : ''}`,
                                        {
                                            href: !button.disabled && button.href ? button.href : null,
                                            onclick: button.disabled ? null : onClick,
                                            target: button.target || null,
                                            'data-identity': button.id || null,
                                            'data-action-name': button.name || null,
                                            rel: button.target === '_blank' ? 'noopener noreferrer' : null,
                                        },
                                        ce('span.tool-icon', ce('img[alt=""]', { src: button.icon })),
                                        ce('span.tool-name', button.noEscapeTitle ? button.title : escapeHtml(button.title)),
                                        button.new ? ce('span.badge-new', this.lmsg('badgeNew')) : '',
                                        Array.isArray(button.additionalComments) ? ce('.span.tool-info',
                                            button.additionalComments
                                                .map(comment => button.noEscape ? comment : escapeHtml(comment))
                                                .join('<br>'),
                                        ) : '',
                                    ),
                                    button.comment ? ce('span.tooltipData', escapeHtml(button.comment)) : '',
                                );
                            }),
                        ),
                    ),
                ),
            ),
        );
    }

    itemCaptionView(item) {
        return ce('.caption',
            ce('.caption-wrap',
                this.itemCaptionHeaderView(item),
                this.itemCaptionServicesView(item),
                this.itemCaptionDataView(item),
                this.itemCaptionAdditionalHtml(item),
            ),
        );
    }

    itemCaptionHeaderView(item) {
        return ce('.caption-header',
            ce('.caption-widget'),
            ce('.caption-header-wrap',
                ce('.caption-main',
                    item.icon ? ce('.caption-icon',
                        ce('span', ce('img[alt=""]', { src: item.icon })),
                    ) : '',
                    this.itemCaptionHeadView(item),
                ),
            ),
        );
    }

    itemCaptionHeadView(item) {
        return ce('.caption-head',
            this.itemCaptionHeadWrapView(item),
            this.itemCaptionSummaryView(item),
            this.itemCaptionToolbarView(item),
        );
    }

    itemCaptionHeadWrapView(item) {
        let labels = [];
        const labelTypes = ['danger', 'warning', 'success', 'info', 'inactive'];

        if (Array.isArray(item.labels) && item.labels.length) {
            labels = item.labels.map(function (label) {
                const type = label.type && labelTypes.indexOf(label.type) > -1 ? label.type : '';
                const cls = type === '' ? '' : (`.label-${type}`);

                return ce(`span.label${cls}`, label.value || '');
            });
        }

        return ce('.caption-head-wrap',
            ce('h2.caption-name',
                ce('span', escapeHtml(item.title)),
            ),
            labels,
            Array.isArray(item.primaryActions) && item.primaryActions.length ? ce('span.caption-head-widget',
                item.primaryActions.map(function (action) {
                    return [ce('a.btn', { href: action.link },
                        action.title,
                    ), ' '];
                }),
            ) : '',
        );
    }

    itemCaptionSummaryView(item) {
        return Array.isArray(item.summary) && item.summary.length ? ce('.caption-summary',
            ce('.caption-summary-wrap',
                item.summary.map(this.summaryItemView.bind(this)),
            ),
        ) : item.summary || '';
    }

    summaryItemView(summaryItem) {
        return ce('span.summary-item', `${summaryItem.name}: <b>${summaryItem.value}</b>`);
    }

    itemCaptionToolbarView(item) {
        return Array.isArray(item.toolbar) && item.toolbar.length ? ce('.caption-toolbar',
            ce('.caption-toolbar-wrap',
                item.toolbar.map(this.toolbarButtonView.bind(this)),
            ),
        ) : '';
    }

    toolbarButtonView(button) {
        return ce(`a.i-link${button.action ? `[data-item-action="${button.action}"]` : ''}`, { href: button.link || null },
            button.icon || button.iconClass ? [ce(`i${button.iconClass ? `.${button.iconClass}` : 'icon'}`,
                button.icon ? ce('img[alt=""]', { src: button.icon }) : '',
            ), ' '] : '',
            ce('span', escapeHtml(button.title)),
        );
    }

    itemCaptionServicesView(item) {
        return Array.isArray(item.services) ? item.services.map(this.itemServiceView.bind(this)) : '';
    }

    itemServiceView(service) {
        return ce(`.caption-services${service.class ? `.${service.class}` : ''}`, { style: service.hidden ? 'display:none' : '' },
            ce('.caption-services-wrap',
                service.header ? ce(service.headerClass ? `.${service.headerClass}` : '',
                    service.closeUrl ? ce('span.close', { 'data-close-url': service.closeUrl }) : '',
                    service.header,
                ) : '',
                this.itemServiceBlockView(service),
            ),
        );
    }

    renderItemServiceView(service) {
        const type = service.type
            .replace(/(^\w|-\w)/g, ch => ch.toUpperCase())
            .replaceAll('-', '');
        return this[`itemService${type}View`](service);
    }

    itemServiceBlockView(service) {
        if (service.grid) {
            const columns = service.grid.reduce(function (columns, service) {
                return columns + (service.colspan || 1);
            }, 0);
            return ce(`.b-grid${service.grid.length > 1 ? `.b-grid-${columns}` : ''}`,
                ce('ul.b-grid-list',
                    service.grid.map(function (subService) {
                        return ce(`li.b-grid-item${service.grid.length > 1 && subService.colspan ? `.b-grid-item-${subService.colspan}` : ''}`,
                            ce('.b-grid-item-wrap', this.itemServiceBlockView(subService)),
                        );
                    }.bind(this)),
                ),
            );
        }

        if (service.type) {
            return this.renderItemServiceView(service);
        }

        let icon = ce('img[alt=""]', { src: service.icon });
        let name = escapeHtml(service.title);
        if (service.href) {
            icon = ce(`a${service.newWindow ? '[target=_blank]' : ''}`, {
                href: service.href,
                onclick: this.navigate,
            }, icon);
            name = ce(`a${service.newWindow ? '[target=_blank]' : ''}`, {
                href: service.href,
                onclick: this.navigate,
            }, name);
        }

        const description = service.description ? service.description : '';
        return ce('.caption-service-block',
            ce('span.caption-service-title',
                ce('i.caption-service-icon', icon),
                ce('span.caption-service-name', name, description),
            ),
            Array.isArray(service.links) && service.links.length ? ce('.caption-service-toolbar',
                service.links.map(function (link) {
                    let linkView;
                    if (link.childLinks && link.childLinks.length) {
                        linkView = ce('.btn-group',
                            ce('a.btn', {
                                href: link.href,
                                target: link.newWindow ? '_blank' : null,
                            }, escapeHtml(link.title)),
                            ce('button.btn.dropdown-toggle[type=button]', ce('em.caret')),
                            ce('ul.dropdown-menu',
                                link.childLinks.map(function (childLink) {
                                    return ce('li',
                                        ce('a', {
                                            href: childLink.href,
                                            target: childLink.newWindow ? '_blank' : null,
                                        }, escapeHtml(childLink.title)),
                                    );
                                }),
                            ),
                        );
                    } else {
                        const icon = link.icon ? [ce('i.icon', ce('img[alt=""]', { src: link.icon })), ' '] : '';
                        if (link.isSimpleText) {
                            linkView = ce('span',
                                icon,
                                link.noEscape ? link.info : escapeHtml(link.info),
                            );
                        } else if (link.isSimpleLink) {
                            linkView = ce(`span${link.spanClass ? `.${link.spanClass}` : ''}`,
                                icon,
                                `${escapeHtml(link.info)} `,
                                ce('a', {
                                    href: link.href,
                                    class: link.class ? escapeHtml(link.class) : null,
                                    onclick: link.onClick ? escapeHtml(link.onClick) : null,
                                    target: link.newWindow ? '_blank' : null,
                                }, escapeHtml(link.title)),
                            );
                        } else {
                            linkView = ce('a.btn', {
                                href: link.href,
                                class: link.class ? escapeHtml(link.class) : null,
                                onclick: link.onClick ? escapeHtml(link.onClick) : null,
                                target: link.newWindow ? '_blank' : null,
                                id: link.id ? escapeHtml(link.id) : null,
                                name: link.name ? escapeHtml(link.name) : null,
                            }, icon, escapeHtml(link.title));
                        }
                    }

                    return [
                        link.newLine ? ce('br') : '',
                        linkView,
                        ' ',
                    ];
                }),
            ) : '',
        );
    }

    itemCaptionDataView(item) {
        if (!item.data || !item.data.length) {
            return '';
        }

        return ce('.caption-data',
            ce('.caption-data-wrap',
                item.data.map(function (data, index) {
                    return ce('.form-group',
                        ce(`label.control-label[for=active-list-data-${item.id}-${index}]`, data.label),
                        ce(`textarea#active-list-data-${item.id}-${index}.form-control.code[rows=${data.linesNumber}][readonly]`, escapeHtml(data.text)),
                    );
                }),
            ),
        );
    }

    itemCaptionAdditionalHtml(item) {
        if (!item.additionalHtml) {
            return '';
        }

        return ce('.caption-data',
            ce('.caption-data-wrap', item.additionalHtml),
        );
    }

    getItemElement(item) {
        return this._componentElement.querySelector(`#active-list-item-${String(item.id).replace(/:/g, '\\:')}`);
    }

    _addEvents() {
        this._componentElement.querySelectorAll('.dropdown-toggle').forEach(element => {
            element.addEventListener('click', event => {
                event.stopPropagation();
                event.preventDefault();
                const hadClassName = event.target.closest('.btn-group').classList.contains('btn-group-open');
                document.querySelectorAll('.active-list .dropdown-toggle').forEach(elem => {
                    elem.closest('.btn-group').classList.remove('btn-group-open');
                });
                if (!hadClassName) {
                    event.target.closest('.btn-group').classList.add('btn-group-open');
                }
            });
            document.body.addEventListener('click', () => {
                element.closest('.btn-group').classList.remove('btn-group-open');
            });
        });

        this._componentElement.querySelectorAll('.caption-services .close').forEach(element => {
            element.addEventListener('click', event => {
                event.stopPropagation();
                event.preventDefault();
                const { closeUrl } = event.target.dataset;
                event.target.closest('.caption').querySelectorAll('.caption-services').forEach(element => {
                    element.style.display = '';
                });
                const servicesEl = event.target.closest('.caption-services');
                servicesEl.parentNode.removeChild(servicesEl);
                api.post(prepareUrl(closeUrl));
            });
        });
    }

    disableItem(id) {
        this.getItemElement({ id }).querySelector('.content-blocker').style.display = '';
    }

    enableItem(id) {
        this.getItemElement({ id }).querySelector('.content-blocker').style.display = 'none';
    }

    scrollToItem(el, duration) {
        const startTime = new Date().getTime();
        const from = document.viewport.getScrollOffsets().top;
        let to = Element.cumulativeOffset(el).top - Element.cumulativeOffset(document.querySelector('.pul-layout__main')).top - 40;

        if (document.getElementById('classic-mode-navigation')) {
            to -= Element.getHeight(document.getElementById('classic-mode-navigation'));
        }

        const frame = () => {
            let progress = duration ? ((new Date().getTime() - startTime) / duration) : 1;
            if (progress > 1) {
                progress = 1;
            }

            window.scrollTo(0, Math.round(from + ((to - from) * progress)));

            if (progress < 1) {
                setTimeout(frame, 50);
            }
        };
        frame();
    }

    getItemIdFromUrl(url) {
        const m = url.match('/id/([0-9]+)');
        return m && m[1];
    }

    render() {
        super.render();

        const id = this.getItemIdFromUrl(decodeURIComponent(this.pageUrl));
        if (!id) {
            return;
        }

        const el = this.getItemElement({ id });
        if (el) {
            pageData().status?.forEach(({ status, content, ...props }) => {
                addStatusMessage(status, content, {
                    ...props,
                    title: Locale.getSection('components.status').lmsg(status),
                    renderTo: el.querySelector('.caption-header-wrap'),
                    renderMode: 'top',
                });
            });
            pageData({ ...pageData(), status: [] });
            this.scrollToItem(el, 10);
        }
    }
}
